import React, { useEffect, useRef, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

interface GalleryProps {
  location: string;
}

interface GalleryImage {
  src: string;
  description?: string;
}

const Gallery: React.FC<GalleryProps> = ({ location }) => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [slideshow, setSlideshow] = useState(false);
  const touchStartX = useRef<number | null>(null);

  const closeModal = () => {
    setActiveIndex(null);
    setSlideshow(false);
  };

  useEffect(() => {
    let mounted = true;

    const loadImages = async () => {
      try {
        const response = await fetch(`/images/${location}/photos.json`);
        if (!response.ok) throw new Error("No manifest found");

        const list = await response.json();

        if (mounted) {
          setImages(
            list.map((item: any) => ({
              src: `/images/${location}/${item.file}`,
              description: item.description,
            }))
          );
        }
      } catch (err) {
        console.warn("Gallery manifest not found or empty:", err);
        if (mounted) setImages([]);
      }
    };

    loadImages();
    return () => {
      mounted = false;
    };
  }, [location]);

  useEffect(() => {
    if (activeIndex === null) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") setActiveIndex((i) => (i! + 1) % images.length);
      if (e.key === "ArrowLeft")
        setActiveIndex((i) => (i! - 1 + images.length) % images.length);
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [activeIndex, images.length]);

  useEffect(() => {
    if (!slideshow || activeIndex === null) return;

    const timer = setInterval(() => {
      setActiveIndex((i) => (i! + 1) % images.length);
    }, 2500);

    return () => clearInterval(timer);
  }, [slideshow, activeIndex, images.length]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartX.current) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;

    if (diff > 40) setActiveIndex((i) => (i! + 1) % images.length);
    if (diff < -40)
      setActiveIndex((i) => (i! - 1 + images.length) % images.length);

    touchStartX.current = null;
  };

  if (!images.length) {
    return (
      <div style={{ textAlign: "center", padding: "60px", opacity: 0.7 }}>
        No photos for this location yet.
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "12px" }}>
      {/* Masonry Layout */}
      <div style={{ columnCount: 4, columnGap: "14px" }}>
        {images.map((img, i) => (
          <img
            key={img.src}
            src={img.src}
            loading="lazy"
            onClick={() => setActiveIndex(i)}
            style={{
              width: "100%",
              marginBottom: "14px",
              borderRadius: "12px",
              cursor: "zoom-in",
              boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
              transition: "transform 0.2s ease, filter 0.3s",
              filter: "blur(4px)",
            }}
            onLoad={(e) => (e.currentTarget.style.filter = "blur(0)")}
          />
        ))}
      </div>

      {/* Zoom Modal */}
      {activeIndex !== null && (
        <div
          onClick={closeModal}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
          >
            <TransformWrapper
              initialScale={1}
              minScale={1}
              maxScale={2.5}
              wheel={{ step: 0.1 }}
              doubleClick={{ disabled: false, mode: "zoomIn", step: 1.5, animationTime: 200 }}
              panning={{ velocityDisabled: true }}
            >
              {({ resetTransform }) => (
                <>
                  <TransformComponent>
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <img
                        src={images[activeIndex].src}
                        style={{
                          maxWidth: "92vw",
                          maxHeight: "80vh",
                          borderRadius: "18px",
                          boxShadow: "0 30px 80px rgba(0,0,0,0.6)",
                          cursor: "grab",
                          userSelect: "none",
                        }}
                        onMouseDown={(e) =>
                          (e.currentTarget.style.cursor = "grabbing")
                        }
                        onMouseUp={(e) =>
                          (e.currentTarget.style.cursor = "grab")
                        }
                      />

                      {/* ✅ DESCRIPTION OVERLAY */}
                      {images[activeIndex].description && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: "12px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "rgba(0,0,0,0.55)",
                            color: "#fff",
                            padding: "8px 14px",
                            borderRadius: "10px",
                            fontSize: "15px",
                            maxWidth: "90%",
                            textAlign: "center",
                            backdropFilter: "blur(4px)",
                            pointerEvents: "none",
                          }}
                        >
                          {images[activeIndex].description}
                        </div>
                      )}
                    </div>
                  </TransformComponent>

                  {/* ✅ RESET BUTTON */}
                  <button
                    onClick={() => resetTransform()}
                    style={{
                      marginTop: "12px",
                      padding: "8px 16px",
                      borderRadius: "10px",
                      border: "none",
                      background: "rgba(255,255,255,0.15)",
                      color: "#fff",
                      fontWeight: 600,
                      cursor: "pointer",
                      backdropFilter: "blur(6px)",
                    }}
                  >
                    Reset
                  </button>
                </>
              )}
            </TransformWrapper>

            {/* Controls */}
            <div
              style={{
                marginTop: "16px",
                display: "flex",
                gap: "14px",
              }}
            >
              <button
                onClick={() => setSlideshow(!slideshow)}
                style={{
                  padding: "10px 18px",
                  borderRadius: "10px",
                  border: "none",
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                  backdropFilter: "blur(6px)",
                }}
              >
                {slideshow ? "Stop" : "Play"}
              </button>

              <button
                onClick={closeModal}
                style={{
                  padding: "10px 18px",
                  borderRadius: "10px",
                  border: "none",
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  cursor: "pointer",
                  fontWeight: 600,
                  backdropFilter: "blur(6px)",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;   